import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../utils/api';
import { TableSkeletonRows, EmptyState, Modal, ConfirmDialog, Badge, sessionTypeBadgeMap, useToast, claimStatusBadgeMap, InlineSpinner, SortTh, useSort, usePagination, Pagination } from '../../components/UI';
import { KpiCard, SectionIcon } from '../../components/TrainerUI';
import { fmtDate, fmtHours } from '../../utils/format';
import { TaeppTitle, TaeppPill, TaeppNote } from '../../components/TaeppUI';
import SignClaimModal from '../../components/SignClaimModal';

const CANCELLABLE =new Set(['under_review']);
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CLAIM_FEE_TYPE_MAP = {
  attendance: { label: 'Attendance', cls: 'badge-present' },
  assessment: { label: 'Assessment', cls: 'badge-graduated' },
  course_preview: { label: 'Course Preview', cls: 'badge-active' },
  interview: { label: 'Interview', cls: 'badge-late' },
};

function localISODate(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtMoney(v) {
  const n = Number(v);
  return isNaN(n) ? '—' : `S$${n.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Assessment sessions are paid per hour (Duration / Final) or per student
// (Per pax). The QR system stores the basis as fa_1_2 / fa_2_2 / NULL while
// pending rows and fee rates use per_pax / final / duration; both are accepted.
const ASSESSMENT_BASIS_LABELS = {
  duration: 'None',
  per_pax: 'F.A. 1.2.',
  final: 'F.A. 2.2.',
};
// Badge look matches the Fee Rates page; Duration is the plain Assessment badge.
const ASSESSMENT_BADGE_KEY = { duration: 'assessment', per_pax: 'assessment_per_pax', final: 'assessment_final' };
const SESSION_TYPE_BADGE_MAP = {
  ...sessionTypeBadgeMap,
  assessment_per_pax: { label: 'Assessment F.A. 1.2.', cls: 'badge-fee-pax' },
  assessment_final: { label: 'Assessment F.A. 2.2.', cls: 'badge-fee-final' },
};
const QR_BASIS_TO_RATE_BASIS = { fa_1_2: 'per_pax', fa_2_2: 'final' };

function itemAssessmentBasis(item) {
  if (item.assessment_basis) return item.assessment_basis;
  if (sessionTypeKey(item.session_type) !== 'assessment') return null;
  return QR_BASIS_TO_RATE_BASIS[item.session_assessment_basis] || 'duration';
}

// Distinct assessment bases across a pending row or a submitted claim's sessions.
function claimAssessmentBases(claim) {
  // Submitted claims carry per-session items (the header has no basis of its
  // own); pending rows are the session itself.
  return [...new Set((claim.items || [claim]).map(itemAssessmentBasis).filter(Boolean))];
}

function claimRateUnit(claim) {
  return claimAssessmentBases(claim).includes('per_pax') ? 'pax' : 'hr';
}

function claimRateLabel(claim) {
  if (claim?.fee_type === 'course_preview' || claim?.fee_type === 'interview') return '—';
  return claim?.fee_rate_id == null ? 'Not set' : `${fmtMoney(claim.rate)}/${claimRateUnit(claim)}`;
}

function claimAmountLabel(claim) {
  const amount = Number(claim?.amount);
  if (!Number.isFinite(amount) || amount <= 0) return 'Pending Amount';
  return fmtMoney(claim.amount);
}

// Builds a Teaching Hour Claims row straight from the pending-session data we
// already have client-side, so a successful submit can be reflected instantly
// without a round trip back through GET /trainer/claims. Handles both a
// single-session submit and a bulk submit covering several sessions — a bulk
// submit is ONE claim record, so `items` holds every session it covers and
// the header fields (module/intake/rate) only resolve to a single value when
// there's exactly one item.
function claimRowFromPending(items, id, remarks) {
  const list = Array.isArray(items) ? items : [items];
  const totalHours = Number(list.reduce((sum, p) => sum + Number(p.hours), 0).toFixed(2));
  const totalAmount = Number(list.reduce((sum, p) => sum + Number(p.amount || 0), 0).toFixed(2));
  const earliestDate = list.reduce((min, p) => (p.session_date < min ? p.session_date : min), list[0].session_date);
  const single = list.length === 1 ? list[0] : null;
  return {
    id,
    session_count: list.length,
    items: list.map(p => ({
      session_id: p.session_id, module_code: p.module_code, module_name: p.module_name, session_type: p.session_type,
      intake_code: p.intake_code, session_date: p.session_date, hours: p.hours, rate: p.rate_per_hour, amount: p.amount,
      assessment_basis: p.assessment_basis,
    })),
    module_code: single?.module_code ?? null,
    module_name: single ? single.module_name : [...new Set(list.map(p => p.module_name))].join(', '),
    fee_type: 'attendance',
    session_type: single?.session_type ?? null,
    intake_code: single?.intake_code ?? null,
    claim_date: earliestDate,
    hours: totalHours,
    fee_rate_id: single?.fee_rate_id ?? null,
    rate: single ? single.rate_per_hour : 0,
    amount: totalAmount,
    status: 'under_review',
    remarks: remarks || null,
    submitted_at: new Date().toISOString(),
  };
}

// C<YY><MM>/<seq> — mirrors the server's claimPdf.service.js formatting.
// seq (claim_seq) is only assigned once the claim is actually persisted, so
// an optimistically-added row (see claimRowFromPending) shows nothing until
// the background refresh in handleBulkSubmit lands the real data.
function fmtClaimNo(c) {
  if (c.claim_seq == null) return '—';
  const d = new Date(c.submitted_at || c.created_at);
  const yy = String(d.getFullYear()).slice(-2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `C${yy}${mm}/${String(c.claim_seq).padStart(4, '0')}`;
}

function fmtTime(t) {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

const sessionTypeKey = value => String(value || '').trim().toLowerCase();

function ClaimSessionTypes({ claim }) {
  const types = [...new Set([
    claim.session_type,
    ...(claim.items || []).map(item => item.session_type),
  ].map(sessionTypeKey).filter(Boolean))];
  if (!types.length) return '—';
  // An assessment's basis is part of its badge (same look as the Fee Rates
  // page): "Assessment", "Assessment F.A. 1.2." or "Assessment F.A. 2.2.".
  const bases = claimAssessmentBases(claim);
  const badges = types.flatMap(type => {
    if (type !== 'assessment') return [type];
    const keys = bases.map(b => ASSESSMENT_BADGE_KEY[b] || 'assessment');
    return keys.length ? [...new Set(keys)] : ['assessment'];
  });
  return <span className="d-inline-flex flex-wrap gap-1">{badges.map(key => (
    <Badge key={key} value={key} map={SESSION_TYPE_BADGE_MAP} />
  ))}</span>;
}

// Session-based claims are stored as fee_type 'attendance' (the admin side
// treats a stored 'assessment' as a manual claim), so the type shown here comes
// from the sessions inside: all Assessment → Assessment, all Training →
// Attendance, a mix → both badges.
function ClaimFeeType({ claim }) {
  const type = claim.fee_type || (claim.session_id || (claim.items || []).length ? 'attendance' : null);
  if (!type) return '—';
  if (type !== 'attendance') return <Badge value={type} map={CLAIM_FEE_TYPE_MAP} />;
  const sessionTypes = (claim.items?.length ? claim.items.map(i => i.session_type) : [claim.session_type]).map(sessionTypeKey);
  const keys = [...new Set(sessionTypes.map(t => (t === 'assessment' ? 'assessment' : 'attendance')))];
  return <span className="d-inline-flex flex-wrap gap-1">{keys.map(key => <Badge key={key} value={key} map={CLAIM_FEE_TYPE_MAP} />)}</span>;
}

// One "Details" cell for every claim type, so Interview / Course Preview rows
// don't need a column of dashes for module, session type, hours and rate.
function ClaimDetails({ claim }) {
  const muted = { fontSize: 12, color: 'var(--sms-txt2)' };
  if (claim.fee_type === 'interview') {
    return claim.pax != null ? `${claim.pax} pax` : '—';
  }
  if (claim.fee_type === 'course_preview') return '—';

  const codes = [...new Set([
    claim.module_code,
    ...(claim.items || []).map(item => item.module_code),
  ].filter(Boolean))];
  const multi = claim.session_count > 1;
  const title = multi
    ? <span title={(claim.items || []).map(it => `${fmtDate(it.session_date)} · ${it.module_name || it.module_code}`).join('\n')}>
        {claim.session_count} sessions {claim.module_name ? <span style={muted}>({claim.module_name})</span> : null}
      </span>
    : (claim.module_code ? `${claim.module_code} - ${claim.module_name || '—'}` : (claim.module_name || '—'));
  const rate = !multi && claim.fee_rate_id != null ? ` · ${claimRateLabel(claim)}` : '';
  return (
    <div>
      <div style={{ fontWeight: 500 }}>{title}{multi && codes.length ? <span style={muted}> · {codes.join(', ')}</span> : null}</div>
      {/* -0.65em at the badge's 11px font = its inner left padding, so the
          pill's text (not its background edge) lines up with the title above. */}
      <div className="d-flex flex-wrap align-items-center gap-1" style={{ marginTop: 3, marginLeft: -7 }}>
        <ClaimSessionTypes claim={claim} />
        {/* +7px cancels the row's -7px shift, so when the hours wrap onto their
            own line they align with the title and badge text, not left of it. */}
        <span style={{ ...muted, marginLeft: 7 }}>{fmtHours(claim.hours, { decimals: 2, unit: true })}{rate}</span>
      </div>
    </div>
  );
}

export default function TrainerClaimPage() {
  const toast = useToast();
  const location = useLocation();
  const autoSelectApplied = useRef(false);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [cancelId, setCancelId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [printingId, setPrintingId] = useState(null);
  const currentYear = new Date().getFullYear();
  const [claimYear, setClaimYear] = useState(currentYear);
  const [pendingMonth, setPendingMonth] = useState('all');
  const [pendingYear, setPendingYear] = useState('all');
  const [claimStatus, setClaimStatus] = useState('all');
  const [exportOpen, setExportOpen] = useState(false);
  const [exportRange, setExportRange] = useState({ from: '', to: '' });
  const [exportNote, setExportNote] = useState('');
  const [exporting, setExporting] = useState(false);
  const [taeppRequests, setTaeppRequests] = useState([]);
  const [taeppLoading, setTaeppLoading] = useState(true);
  const [taeppCancelId, setTaeppCancelId] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualError, setManualError] = useState('');
  const [manualSign, setManualSign] = useState(false);
  const [manualForm, setManualForm] = useState({ fee_type: 'course_preview', claim_date: localISODate(), pax: '', remarks: '' });

  // Opens the PDF in a new tab (browser's own viewer) so the trainer can
  // preview it before deciding to save — not an immediate forced download.
  const printClaim = async id => {
    setPrintingId(id);
    try {
      const res = await api.get(`/trainer/claims/${id}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch {
      toast('Failed to generate PDF', 'error');
    } finally {
      setPrintingId(null);
    }
  };

  const load = () => {
    setLoading(true);
    api.get('/trainer/claims').then(r => setRows(r.data.data || []))
      .catch(() => toast('Failed to load claims', 'error'))
      .finally(() => setLoading(false));
  };
  const loadPending = () => {
    setPendingLoading(true);
    api.get('/trainer/claims/pending').then(r => setPending(r.data.data || []))
      .catch(() => toast('Failed to load pending claims', 'error'))
      .finally(() => setPendingLoading(false));
  };

  const loadTaepp = () => {
    setTaeppLoading(true);
    api.get('/trainer/taepp-requests').then(r => setTaeppRequests(r.data.data || []))
      .catch(() => toast('Failed to load TAEPP requests', 'error'))
      .finally(() => setTaeppLoading(false));
  };

  useEffect(() => { load(); loadPending(); loadTaepp(); }, []);

  // Drop any selection that's no longer a claimable pending session (e.g. it
  // got claimed elsewhere, or the fee rate backing it was removed).
  useEffect(() => {
    const claimableIds = new Set(pending.filter(p => p.claimable).map(p => p.session_id));
    setSelected(prev => {
      const next = new Set([...prev].filter(id => claimableIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [pending]);

  // Arriving from the dashboard's "Submit Claim" nudge pre-ticks every
  // claimable session so the trainer just has to hit "Submit Selected".
  // Applied once (ref guard) so it doesn't re-tick after the trainer
  // deliberately unchecks something while pending data keeps refreshing.
  useEffect(() => {
    if (!location.state?.autoSelectAll || autoSelectApplied.current || pendingLoading) return;
    const claimableIds = pending.filter(p => p.claimable).map(p => p.session_id);
    if (claimableIds.length) {
      setSelected(new Set(claimableIds));
      autoSelectApplied.current = true;
    }
  }, [location.state, pending, pendingLoading]);

  const toggleSelect = sessionId => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId); else next.add(sessionId);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected(prev => prev.size === visibleClaimablePending.length
      ? new Set()
      : new Set(visibleClaimablePending.map(p => p.session_id)));
  };

  // Bulk submit hits the bulk endpoint once, which creates a single claim
  // record covering every selected session — not one claim per session.
  // Sessions the server couldn't claim (race, rate removed since ticking the
  // box, etc.) come back in `failed` and are left in Pending Claims.
  const handleBulkSubmit = async details => {
    setBulkSaving(true);
    const ids = [...selected];
    try {
      const r = await api.post('/trainer/claims/bulk', { session_ids: ids, remarks: '', ...details });
      const failedIds = new Set((r.data.failed || []).map(f => f.session_id));
      const succeededItems = pending.filter(p => ids.includes(p.session_id) && !failedIds.has(p.session_id));
      if (succeededItems.length) {
        toast(`Claim submitted covering ${succeededItems.length} session${succeededItems.length === 1 ? '' : 's'}`, 'success');
        setRows(prev => [claimRowFromPending(succeededItems, r.data.id, ''), ...prev]);
        setPending(prev => prev.filter(p => !succeededItems.some(s => s.session_id === p.session_id)));
        // The optimistic row above has no claim_seq yet (assigned server-side
        // at insert) — refresh in the background so its claim number appears.
        load();
      }
      if (failedIds.size) toast(`${failedIds.size} session${failedIds.size === 1 ? '' : 's'} could not be claimed`, 'error');
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to submit claims', 'error');
    } finally {
      setBulkSaving(false);
      setBulkConfirm(false);
      setSelected(new Set());
    }
  };

  const handleCancel = async () => {
    const id = cancelId;
    try {
      await api.put(`/trainer/claims/${id}/cancel`);
      toast('Claim cancelled', 'success');
      setRows(prev => prev.map(r => r.id === id ? { ...r, status: 'cancelled' } : r));
      // A cancelled claim's session becomes resubmittable server-side, but
      // whether it's actually claimable depends on rate/date matching we
      // can't recompute client-side — refresh just the pending list to pick
      // it back up, rather than the whole page.
      loadPending();
    } catch (err) {
      toast(err.response?.data?.message || 'Cancel failed', 'error');
    } finally {
      setCancelId(null);
    }
  };

  const openManualClaim = () => {
    setManualForm({ fee_type: 'course_preview', claim_date: localISODate(), pax: '', remarks: '' });
    setManualError('');
    setManualOpen(true);
  };

  const manualIsInterview = manualForm.fee_type === 'interview';
  const manualPreview = { manual: { fee_type: manualForm.fee_type, claim_date: manualForm.claim_date, pax: manualIsInterview ? Number(manualForm.pax) : null } };

  // The form only collects the details; the claim is created from the review-and-sign step.
  const handleManualSubmit = e => {
    e.preventDefault();
    if (manualIsInterview && (!Number.isInteger(Number(manualForm.pax)) || Number(manualForm.pax) <= 0)) {
      setManualError('Enter the number of pax interviewed (a whole number, at least 1).');
      return;
    }
    setManualError('');
    setManualOpen(false);
    setManualSign(true);
  };

  const handleManualSign = async details => {
    setManualSaving(true);
    try {
      await api.post('/trainer/claims/manual', {
        fee_type: manualForm.fee_type,
        pax: manualIsInterview ? Number(manualForm.pax) : null,
        claim_date: manualForm.claim_date,
        remarks: manualForm.remarks.trim() || null,
        ...details,
      });
      toast('Claim submitted for review', 'success');
      setManualSign(false);
      load();
    } catch (err) {
      // Back to the form, where the message sits next to the fields it may be about.
      setManualError(err.response?.data?.message || 'Failed to submit claim');
      setManualSign(false);
      setManualOpen(true);
    } finally {
      setManualSaving(false);
    }
  };

  // ── Request TAEPP ─────────────────────────────────────────────────────
  // The range filters on claim_date. The live summary below the date pickers
  // is computed from the rows already loaded; the request itself is recorded by
  // POST /trainer/taepp-requests and the PDF is only sent once an approver signs off.
  const openExport = () => {
    // Start from the earliest approved claim (not "this month"), so nothing
    // approved is left out of the file unless the trainer narrows the range.
    const today = new Date();
    const approvedDates = rows.filter(r => r.status === 'approved').map(r => String(r.claim_date || '').slice(0, 10)).filter(Boolean).sort();
    setExportRange({
      from: approvedDates[0] || localISODate(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: localISODate(today),
    });
    setExportNote('');
    setExportOpen(true);
  };
  const quickRangeFor = key => {
    const t = new Date();
    if (key === 'this_month') return { from: localISODate(new Date(t.getFullYear(), t.getMonth(), 1)), to: localISODate(t) };
    if (key === 'last_month') return { from: localISODate(new Date(t.getFullYear(), t.getMonth() - 1, 1)), to: localISODate(new Date(t.getFullYear(), t.getMonth(), 0)) };
    return { from: localISODate(new Date(t.getFullYear(), 0, 1)), to: localISODate(t) };
  };
  const setQuickRange = key => setExportRange(quickRangeFor(key));
  const exportRangeInvalid = !exportRange.from || !exportRange.to || exportRange.from > exportRange.to;
  const exportMatches = exportRangeInvalid ? [] : rows.filter(r => {
    const d = String(r.claim_date || '').slice(0, 10);
    return d >= exportRange.from && d <= exportRange.to && r.status === 'approved';
  });
  const exportTotal = exportMatches.reduce((sum, r) => sum + Number(r.amount || 0), 0);

  const handleExport = async () => {
    setExporting(true);
    try {
      await api.post('/trainer/taepp-requests', { from: exportRange.from, to: exportRange.to, note: exportNote.trim() });
      toast('TAEPP request submitted for approval', 'success');
      setExportOpen(false);
      loadTaepp();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to submit TAEPP request', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleTaeppCancel = async () => {
    const id = taeppCancelId;
    try {
      await api.put(`/trainer/taepp-requests/${id}/cancel`);
      toast('TAEPP request cancelled', 'success');
      setTaeppRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'cancelled' } : r));
    } catch (err) {
      toast(err.response?.data?.message || 'Cancel failed', 'error');
      if (err.response?.status === 409) loadTaepp();   // the approver got there first: show the real status
    } finally {
      setTaeppCancelId(null);
    }
  };

  const paidTotal = rows.filter(r => r.status === 'paid').reduce((sum, r) => sum + Number(r.amount), 0);
  const claimablePending = pending.filter(p => p.claimable);

  // Pending Claims month/year filter — "All" by default so nothing already
  // in view disappears; sessions here are always past (backend only returns
  // ended sessions), so no future-date restriction is needed like the claim
  // number filter above.
  const pendingYearOptions = [...new Set(pending.map(p => new Date(p.session_date).getFullYear()))].sort((a, b) => b - a);
  const filteredPending = pending.filter(p => {
    const d = new Date(p.session_date);
    if (pendingMonth !== 'all' && d.getMonth() + 1 !== Number(pendingMonth)) return false;
    if (pendingYear !== 'all' && d.getFullYear() !== Number(pendingYear)) return false;
    return true;
  });
  // Scoped to the filtered set so "select all" / the header checkbox only
  // ever act on what's actually visible.
  const visibleClaimablePending = filteredPending.filter(p => p.claimable);
  const nextWindowOpen = filteredPending.filter(p => !p.window_open && p.window_opens_on)
    .reduce((min, p) => (!min || p.window_opens_on < min ? p.window_opens_on : min), null);
  const hasLateWindow = filteredPending.some(p => p.window_late);
  const {
    paged: pagedPending, page: pendingPage, setPage: setPendingPage,
    pageSize: pendingPageSize, setPageSize: setPendingPageSize,
    totalPages: pendingTotalPages, total: pendingTotal, start: pendingStart,
  } = usePagination(filteredPending, 5);

  // Year filter — options come from years actually present in the data
  // (plus the current year, always available even with no claims yet),
  // never a year later than today.
  const claimYearOptions = [...new Set(rows.map(r => new Date(r.submitted_at || r.created_at).getFullYear()).concat(currentYear))]
    .filter(y => y <= currentYear)
    .sort((a, b) => b - a);
  const yearRows = rows.filter(r => new Date(r.submitted_at || r.created_at).getFullYear() === claimYear)
    .filter(r => claimStatus === 'all' || r.status === claimStatus)
    .map(r => ({ ...r, claim_no: fmtClaimNo(r) }));
  const { sorted: sortedRows, sortKey, sortDir, toggleSort } = useSort(yearRows, 'claim_no', 'desc');
  const { paged: pagedRows, page, setPage, pageSize, setPageSize, totalPages, total, start } = usePagination(sortedRows, 5);

  return (
    <>
      <div className="trn-topbar">
        <div className="trn-topbar-title">Claim</div>
        <div className="trn-topbar-sub">Submit and track teaching hour claims</div>
      </div>
      <div className="trn-page">
        <div className="trn-stat-row" style={{ maxWidth: 700 }}>
          <KpiCard label="Pending to Submit" value={claimablePending.length} icon="bi-hourglass-split" color="var(--sms-stat-red)"
            caption={claimablePending.length ? `Worth ${fmtMoney(claimablePending.reduce((sum, p) => sum + Number(p.amount || 0), 0))}` : 'All caught up'}
            onClick={claimablePending.length ? () => setSelected(new Set(claimablePending.map(p => p.session_id))) : undefined} />
          <KpiCard label="Claims Submitted" value={rows.length} icon="bi-receipt" color="var(--sms-stat-blue)"
            caption={rows.filter(r => ['under_review', 'verified'].includes(r.status)).length ? `${rows.filter(r => ['under_review', 'verified'].includes(r.status)).length} awaiting decision` : null} />
          <KpiCard label="Total Paid" value={<span style={{ fontSize: 22 }}>{fmtMoney(paidTotal)}</span>} icon="bi-cash-coin" color="var(--sms-stat-green)" />
        </div>

        <div className="trn-readonly-note" style={{ alignItems: 'flex-start', marginBottom: 18 }}>
          <i className="bi bi-info-circle" style={{ color: 'var(--sms-acc)', fontSize: 16, flex: '0 0 auto', marginTop: 1 }} aria-hidden="true" />
          <div>
            <div style={{ color: 'var(--sms-txt)', fontWeight: 700, marginBottom: 4 }}>Claim Submission Guide</div>
            <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--sms-txt)' }}>
              <li>Pending Claims lists your ended, unclaimed Attendance sessions.</li>
              <li>Sessions appear after their scheduled end time. The claim window opens on the configured day of the following month.</li>
              <li>You can submit rows marked <strong>Claimable</strong>. A missing fee rate or a session outside its intake period must be resolved by Admin first.</li>
              <li>Use <strong>Add Course Preview or Interview</strong> for those activities. These claim types do not record hours; a matching fee rate is applied automatically when available.</li>
              <li>Late claims can still be submitted when allowed, but they are marked <strong>Late</strong>. Submit as soon as the window opens.</li>
              <li>Select one or more Claimable sessions and choose <strong>Submit Selected</strong>; selected sessions are combined into one claim.</li>
            </ul>
          </div>
        </div>

        <div className="trn-card">
          <div className="trn-card-head d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h3 className="d-flex align-items-center"><SectionIcon icon="bi-hourglass-split" color="var(--sms-stat-amber)" />Pending Claims</h3>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {(
                <button type="button" className="sms-btn trn-claim-add-btn" style={{ fontSize: 12, padding: '5px 10px' }} onClick={openManualClaim}>
                  <i className="bi bi-plus-lg" /> Add Course Preview or Interview
                </button>
              )}
              <select className="sms-form-control" style={{ width: 'auto', fontSize: 12.5, padding: '5px 8px' }}
                value={pendingMonth} onChange={e => setPendingMonth(e.target.value)}>
                <option value="all">All Months</option>
                {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select className="sms-form-control" style={{ width: 'auto', fontSize: 12.5, padding: '5px 8px' }}
                value={pendingYear} onChange={e => setPendingYear(e.target.value)}>
                <option value="all">All Years</option>
                {pendingYearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              {selected.size > 0 && (
                <button type="button" className="sms-btn sms-btn-primary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => setBulkConfirm(true)}>
                  <i className="bi bi-send" /> Submit Selected ({selected.size})
                </button>
              )}
            </div>
          </div>
          <div className="trn-card-body">
            {!pendingLoading && nextWindowOpen && (
              <div className="d-flex align-items-center gap-2" style={{
                background: 'var(--sms-sur2)', border: '1px dashed var(--sms-bdr)', borderRadius: 8,
                padding: '10px 12px', marginBottom: 12, fontSize: 12.5, color: 'var(--sms-txt2)',
              }}>
                <i className="bi bi-hourglass-split" style={{ color: 'var(--sms-stat-amber)' }} />
                This month's sessions aren't claimable yet — they open on {fmtDate(nextWindowOpen)}.
              </div>
            )}
            {!pendingLoading && hasLateWindow && (
              <div className="d-flex align-items-center gap-2" style={{
                background: 'var(--sms-sur2)', border: '1px dashed var(--sms-bdr)', borderRadius: 8,
                padding: '10px 12px', marginBottom: 12, fontSize: 12.5, color: 'var(--sms-txt2)',
              }}>
                <i className="bi bi-exclamation-triangle" style={{ color: 'var(--sms-stat-amber)' }} />
                Some sessions are past their claim due date — they can still be submitted, but try to catch up soon.
              </div>
            )}
            {!pendingLoading && pending.length === 0 ? (
              <EmptyState icon="bi-check2-circle" message="No ended sessions waiting on a claim" />
            ) : !pendingLoading && filteredPending.length === 0 ? (
              <EmptyState icon="bi-check2-circle" message="No sessions match this filter" />
            ) : (
              <>
              <table className="trn-table trn-claim-table">
                <thead><tr>
                  {(
                    <th style={{ width: 36, textAlign: 'center' }}>
                      {visibleClaimablePending.length > 0 && (
                        <input type="checkbox" className="trn-checkbox"
                          checked={selected.size === visibleClaimablePending.length}
                          onChange={toggleSelectAll} />
                      )}
                    </th>
                  )}
                  <th>Date</th><th>Time</th><th>Intake</th><th>Module</th><th>Session Type</th>
                  <th className="num">Hours</th><th className="num">Rate</th><th className="num">Amount</th><th>Status</th>
                </tr></thead>
                <tbody>
                  {pendingLoading ? <TableSkeletonRows columns={10} /> : pagedPending.map(p => (
                    <tr key={p.session_id}
                      className={selected.has(p.session_id) ? 'trn-row-selected' : undefined}
                      className={!p.claimable ? 'sms-row-muted' : undefined}>
                      {(
                        <td style={{ textAlign: 'center' }}>
                          {p.claimable && (
                            <input type="checkbox" className="trn-checkbox"
                              checked={selected.has(p.session_id)}
                              onChange={() => toggleSelect(p.session_id)} />
                          )}
                        </td>
                      )}
                      <td>{fmtDate(p.session_date)}</td>
                      <td>{fmtTime(p.start_time)} – {fmtTime(p.end_time)}</td>
                      <td>{p.intake_code || '—'}</td>
                      <td style={{ fontWeight: 500 }}>{p.module_code ? `${p.module_code} - ${p.module_name || '—'}` : (p.module_name || '—')}</td>
                      <td><ClaimSessionTypes claim={p} /></td>
                      <td className="num">{fmtHours(p.hours, { decimals: 2 })}</td>
                      <td className="num">
                        {p.rate_per_hour ? `${fmtMoney(p.rate_per_hour)}/${p.rate_unit === 'pax' ? 'pax' : 'hr'}` : '—'}
                      </td>
                      <td className="num" style={{ fontWeight: p.claimable ? 600 : 400 }}>{p.amount !== null ? fmtMoney(p.amount) : '—'}</td>
                      <td>
                        {p.claimable && p.window_late ? (
                          // Past due, but still submittable — badge-late (pre-verified
                          // WCAG AA amber pill in both light/dark themes, same class
                          // already used for "Under Review"/"Registered" elsewhere).
                          <span className="badge rounded-pill badge-late" style={{ fontSize: 12, padding: '4px 9px' }}
                            title={`Past its claim due date (${fmtDate(p.window_due_on)}) but can still be submitted`}>
                            <i className="bi bi-exclamation-triangle" style={{ marginRight: 3 }} /> Late
                            {p.window_due_on && ` · Due ${fmtDate(p.window_due_on)}`}
                          </span>
                        ) : p.claimable ? (
                          <span className="badge rounded-pill badge-present" style={{ fontSize: 12, padding: '4px 9px' }}
                            title={p.window_due_on ? `Claim window due ${fmtDate(p.window_due_on)}` : undefined}>
                            <i className="bi bi-check-circle" style={{ marginRight: 3 }} /> Claimable
                            {p.window_due_on && ` · Due ${fmtDate(p.window_due_on)}`}
                          </span>
                        ) : !p.window_open ? (
                          <span className="badge rounded-pill" style={{ fontSize: 12, padding: '4px 9px', background: 'var(--sms-sur2)', color: 'var(--sms-txt3)' }}
                            title="This month's sessions aren't claimable yet">
                            <i className="bi bi-hourglass-split" style={{ marginRight: 3 }} /> Opens {fmtDate(p.window_opens_on)}
                          </span>
                        ) : p.outside_intake_period ? (
                          <span className="badge rounded-pill" style={{ fontSize: 12, padding: '4px 9px', background: 'var(--sms-sur2)', color: 'var(--sms-txt3)' }}
                            title="This session falls outside its intake's course period">
                            <i className="bi bi-calendar-x" style={{ marginRight: 3 }} /> Outside intake period
                          </span>
                        ) : p.no_attendees ? (
                          <span className="badge rounded-pill" style={{ fontSize: 12, padding: '4px 9px', background: 'var(--sms-sur2)', color: 'var(--sms-txt3)' }}
                            title="Per pax assessments are paid per student, and no attendance was recorded for this session">
                            <i className="bi bi-people" style={{ marginRight: 3 }} /> No attendees
                          </span>
                        ) : (
                          <span className="badge rounded-pill" style={{ fontSize: 12, padding: '4px 9px', background: 'var(--sms-sur2)', color: 'var(--sms-txt3)' }}
                            title={p.assessment_basis
                              ? `No active assessment rate (${ASSESSMENT_BASIS_LABELS[p.assessment_basis]}) covers ${fmtDate(p.session_date)}`
                              : 'No fee rate has been set up for this session yet'}>
                            <i className="bi bi-dash-circle" style={{ marginRight: 3 }} /> No rate set
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pendingTotal > 5 && (
                <Pagination page={pendingPage} totalPages={pendingTotalPages} total={pendingTotal} pageSize={pendingPageSize}
                  setPage={setPendingPage} setPageSize={setPendingPageSize} start={pendingStart} idSuffix="-pending" />
              )}
              </>
            )}
          </div>
        </div>

        <div className="trn-card">
          <div className="trn-card-head d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h3 className="d-flex align-items-center"><SectionIcon icon="bi-clock-history" color="var(--sms-stat-blue)" />Teaching Hour Claims</h3>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <select className="sms-form-control" style={{ width: 'auto', fontSize: 12.5, padding: '5px 8px' }}
                value={claimStatus} onChange={e => setClaimStatus(e.target.value)}>
                <option value="all">All Statuses</option>
                {Object.entries(claimStatusBadgeMap).map(([value, cfg]) => <option key={value} value={value}>{cfg.label}</option>)}
              </select>
              <select className="sms-form-control" style={{ width: 'auto', fontSize: 12.5, padding: '5px 8px' }}
                value={claimYear} onChange={e => setClaimYear(Number(e.target.value))}>
                {claimYearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button type="button" className="sms-btn sms-btn-outline" style={{ fontSize: 12.5, padding: '5px 10px' }}
                disabled={loading || rows.length === 0} onClick={openExport}>
                <i className="bi bi-file-earmark-text" /> Request TAEPP
              </button>
            </div>
          </div>
          <div className="trn-card-body">
            {!loading && rows.length === 0 ? (
              <EmptyState icon="bi-cash-coin" message="You haven't submitted any claims yet" />
            ) : !loading && yearRows.length === 0 ? (
              <EmptyState icon="bi-cash-coin" message={claimStatus === 'all'
                ? `No claims submitted in ${claimYear}`
                : `No ${claimStatusBadgeMap[claimStatus]?.label.toLowerCase() || claimStatus} claims in ${claimYear}`} />
            ) : (
              <>
              <table className="trn-table">
                <thead><tr>
                  <SortTh label="Claim No" field="claim_no" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <th>Claim Type</th>
                  <SortTh label="Date" field="claim_date" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortTh label="Details" field="module_name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <SortTh label="Amount" field="amount" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} style={{ textAlign: 'right' }} />
                  <SortTh label="Status" field="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                  <th></th>
                </tr></thead>
                <tbody>
                  {loading ? <TableSkeletonRows columns={7} /> : pagedRows.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>{c.claim_no}</td>
                      <td><ClaimFeeType claim={c} /></td>
                      <td>{fmtDate(c.claim_date)}</td>
                      <td><ClaimDetails claim={c} /></td>
                      <td className="num">{claimAmountLabel(c)}</td>
                      <td><Badge value={c.status} map={claimStatusBadgeMap} /></td>
                      <td>
                        <div className="d-flex gap-1">
                          {c.status !== 'cancelled' && (
                            <button type="button" className="sms-btn sms-btn-outline-info" title="Print" aria-label="Print"
                              style={{ fontSize: 12, padding: '3px 7px' }}
                              disabled={printingId === c.id} onClick={() => printClaim(c.id)}>
                              {printingId === c.id ? <InlineSpinner /> : <i className="bi bi-printer" />}
                            </button>
                          )}
                          {CANCELLABLE.has(c.status) && (
                            <button type="button" className="sms-btn sms-btn-outline-danger" title="Cancel" aria-label="Cancel"
                              style={{ fontSize: 12, padding: '3px 7px' }} onClick={() => setCancelId(c.id)}>
                              <i className="bi bi-x-circle" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {total > 5 && (
                <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize}
                  setPage={setPage} setPageSize={setPageSize} start={start} idSuffix="-claims" />
              )}
              </>
            )}
          </div>
        </div>

        <div className="trn-card taepp">
          <div className="trn-card-head">
            <h3 className="d-flex align-items-center"><SectionIcon icon="bi-file-earmark-text" color="var(--sms-stat-blue)" />My TAEPP Requests</h3>
          </div>
          <div className="trn-card-body">
            {!taeppLoading && taeppRequests.length === 0 ? (
              <EmptyState icon="bi-file-earmark-text" message="You haven't requested a TAEPP yet" />
            ) : (
              <table className="trn-table">
                <thead><tr>
                  <th>Request</th><th>Period</th><th style={{ textAlign: 'right' }}>Amount</th><th>Status</th><th>Details</th><th></th>
                </tr></thead>
                <tbody>
                  {taeppLoading ? <TableSkeletonRows columns={6} /> : taeppRequests.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{r.request_no}</td>
                      <td>{fmtDate(r.date_from)} – {fmtDate(r.date_to)}</td>
                      <td className="num">{fmtMoney(r.total_amount)}</td>
                      <td><TaeppPill status={r.status} /></td>
                      <td className="tp-cell-sub">
                        {r.status === 'approved' ? (r.emailed_at ? `Signed PDF emailed ${fmtDate(r.emailed_at)}` : 'Approved. The email is on its way.')
                          : r.status === 'rejected' ? `Remarks: ${r.remarks || '—'}`
                          : r.status === 'pending' ? `${r.claim_count} claim${r.claim_count === 1 ? '' : 's'} · waiting for approval` : ''}
                      </td>
                      <td>
                        {r.status === 'pending' && (
                          <button type="button" className="tp-btn sm text-bad" onClick={() => setTaeppCancelId(r.id)}>
                            <i className="bi bi-x-circle" aria-hidden="true" />Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <Modal show={exportOpen} onClose={() => !exporting && setExportOpen(false)}
        title={<TaeppTitle icon="bi-file-earmark-text" title="Request TAEPP" subtitle="Your approver reviews and signs it before it is emailed to you." />}>
        <div className="taepp d-flex flex-column" style={{ gap: 18, minWidth: 'min(420px, 100%)' }}>
          <div>
            <span className="tp-label">Claim Date Range</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label htmlFor="export-from" className="tp-hint" style={{ display: 'block', marginBottom: 4 }}>From</label>
                <input id="export-from" type="date" className="sms-form-control" style={{ width: '100%' }}
                  value={exportRange.from} max={exportRange.to || undefined}
                  onChange={e => setExportRange(r => ({ ...r, from: e.target.value }))} />
              </div>
              <div>
                <label htmlFor="export-to" className="tp-hint" style={{ display: 'block', marginBottom: 4 }}>To</label>
                <input id="export-to" type="date" className="sms-form-control" style={{ width: '100%' }}
                  value={exportRange.to} min={exportRange.from || undefined}
                  onChange={e => setExportRange(r => ({ ...r, to: e.target.value }))} />
              </div>
            </div>
            <div className="tp-chips" style={{ marginTop: 10 }}>
              {[['this_month', 'This month'], ['last_month', 'Last month'], ['this_year', 'This year']].map(([key, label]) => {
                const q = quickRangeFor(key);
                const active = q.from === exportRange.from && q.to === exportRange.to;
                return (
                  <button key={key} type="button" aria-pressed={active} className={`tp-chip${active ? ' on' : ''}`} onClick={() => setQuickRange(key)}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Summary card: what the TAEPP will cover for the chosen range. */}
          {(() => {
            const empty = exportRangeInvalid || !exportMatches.length;
            return (
              <div role="status" className={`tp-card${empty ? ' muted' : ''}`}>
                <i className={`bi ${empty ? 'bi-info-circle' : 'bi-file-earmark-check'}`} aria-hidden="true" />
                <div style={{ minWidth: 0 }}>
                  {exportRangeInvalid ? (
                    <div className="tp-card-main">Choose a start date that is on or before the end date.</div>
                  ) : exportMatches.length ? (
                    <>
                      <div className="tp-card-main">{exportMatches.length} approved claim{exportMatches.length === 1 ? '' : 's'} · {fmtMoney(exportTotal)}</div>
                      <div className="tp-card-sub">{fmtDate(exportRange.from)} – {fmtDate(exportRange.to)}</div>
                    </>
                  ) : (
                    <>
                      <div className="tp-card-main">No approved claims in this range</div>
                      <div className="tp-card-sub">Try a wider date range.</div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}

          <div>
            <label htmlFor="taepp-note" className="tp-label">Note to Approver (Optional)</label>
            <textarea id="taepp-note" className="sms-form-control" rows={2} maxLength={500} style={{ width: '100%' }}
              placeholder="For example: needed for my bank submission."
              value={exportNote} onChange={e => setExportNote(e.target.value)} />
            <div style={{ marginTop: 8 }}>
              <TaeppNote icon="bi-envelope-check">The signed PDF is emailed to you once your request is approved.</TaeppNote>
            </div>
          </div>

          <div className="tp-actions" style={{ marginTop: 0 }}>
            <button type="button" className="tp-btn" onClick={() => setExportOpen(false)} disabled={exporting}>Cancel</button>
            <button type="button" className="tp-btn primary" disabled={exporting || exportRangeInvalid || !exportMatches.length} onClick={handleExport}>
              {exporting ? <InlineSpinner /> : <><i className="bi bi-send" aria-hidden="true" />Submit Request</>}
            </button>
          </div>
        </div>
      </Modal>

      <Modal show={manualOpen} onClose={() => !manualSaving && setManualOpen(false)} title="Add Course Preview or Interview">
        <form onSubmit={handleManualSubmit} noValidate>
          {manualError && <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>{manualError}</div>}
          <div className="d-flex flex-column" style={{ gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', marginBottom: 5 }}>Type *</div>
              <div role="radiogroup" aria-label="Claim type" className="fee-rate-type-options"
                style={{ '--fee-rate-index': manualForm.fee_type === 'interview' ? 1 : 0 }}>
                <span className="fee-rate-type-slider" aria-hidden="true" />
                {Object.entries({ course_preview: 'Course Preview', interview: 'Interview' }).map(([value, label]) => (
                  <label key={value} className={`fee-rate-type-option${manualForm.fee_type === value ? ' selected' : ''}`}>
                    <input type="radio" name="manual_claim_type" value={value} checked={manualForm.fee_type === value}
                      onChange={e => setManualForm(f => ({ ...f, fee_type: e.target.value }))} />
                    <span className="fee-rate-type-dot" aria-hidden="true" />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="manual-claim-date" style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Date *</label>
              <input id="manual-claim-date" type="date" className="sms-form-control" value={manualForm.claim_date} max={localISODate()}
                onChange={e => setManualForm(f => ({ ...f, claim_date: e.target.value }))} required />
            </div>
            {manualForm.fee_type === 'interview' && (
              <div>
                <label htmlFor="manual-claim-pax" style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>No. of Pax *</label>
                <input id="manual-claim-pax" type="number" className="sms-form-control" min="1" step="1" value={manualForm.pax}
                  onChange={e => setManualForm(f => ({ ...f, pax: e.target.value }))} placeholder="e.g. 5" required />
              </div>
            )}
            <div>
              <label htmlFor="manual-claim-remarks" style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Remarks</label>
              <textarea id="manual-claim-remarks" className="sms-form-control" rows="3" value={manualForm.remarks}
                onChange={e => setManualForm(f => ({ ...f, remarks: e.target.value }))} placeholder="Optional details" />
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-end mt-4">
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => setManualOpen(false)} disabled={manualSaving}><i className="bi bi-x-lg" /> Cancel</button>
            <button type="submit" className="sms-btn sms-btn-primary" disabled={manualSaving}>
              <i className="bi bi-pen" /> Review and Sign
            </button>
          </div>
        </form>
      </Modal>

      <SignClaimModal show={manualSign} title="Review and Sign Your Claim"
        previewBody={manualPreview} submitting={manualSaving} onSubmit={handleManualSign}
        onClose={() => { setManualSign(false); setManualOpen(true); }} />

      <SignClaimModal show={bulkConfirm} title="Review and Sign Your Claim"
        subtitle={`${selected.size} session${selected.size === 1 ? '' : 's'} · ${fmtMoney(pending.filter(p => selected.has(p.session_id)).reduce((sum, p) => sum + Number(p.amount || 0), 0))} total`}
        previewBody={{ session_ids: [...selected] }} submitting={bulkSaving} onSubmit={handleBulkSubmit}
        onClose={() => setBulkConfirm(false)} />

      <ConfirmDialog show={!!cancelId} danger message="Cancel this claim? This cannot be undone." onConfirm={handleCancel} onCancel={() => setCancelId(null)} />
      <ConfirmDialog show={!!taeppCancelId} danger message="Cancel this TAEPP request?" onConfirm={handleTaeppCancel} onCancel={() => setTaeppCancelId(null)} />
    </>
  );
}
